/**
 * The phone field — flag, dial code as a fixed prefix, and a searchable country list
 * (`06-DESIGN-SYSTEM.md` §6.8, WP-18). Country data is `./countries.js`, bundled — no API call.
 */

import { COUNTRIES, countryByIso2, searchCountries } from './countries.js';

/**
 * Builds the phone field's DOM and wires its interaction, returning a small controller rather
 * than reaching back into the DOM from outside — the same shape `wizard.js` uses for every
 * other field so the wizard does not need to know each field type's internals.
 *
 * @param {(value: string) => void} onChange Called with the full `+<dial><number>` value on
 *                                            every change, digits-only after the leading `+`.
 * @return {{element: HTMLElement, getValue: () => string, setValue: (value: string) => void}}
 */
export function buildPhoneField( onChange ) {
	let selected = COUNTRIES[ 0 ];

	const wrapper = document.createElement( 'div' );

	wrapper.className = 'anudan-checkout__phone';

	const prefix = document.createElement( 'button' );

	prefix.type = 'button';
	prefix.className = 'anudan-checkout__phone-prefix';
	prefix.setAttribute( 'aria-haspopup', 'listbox' );
	prefix.setAttribute( 'aria-expanded', 'false' );

	const number = document.createElement( 'input' );

	number.type = 'tel';
	number.className = 'anudan-checkout__field-input anudan-checkout__phone-number';
	number.setAttribute( 'autocomplete', 'tel-national' );
	number.setAttribute( 'inputmode', 'tel' );
	// `wizard.js`'s own "Phone" text is a <span>, not a <label for="…">, since this field is a
	// composite (country prefix + number) rather than one control a single <label> wraps — an
	// axe-core scan of criterion 11 (09-PHASE-4-BUILD.md) found this input with no accessible
	// name at all as a result. `aria-label` is the same fix `search`, below, already uses for
	// the identical reason.
	number.setAttribute(
		'aria-label',
		window.wp && window.wp.i18n ? window.wp.i18n.__( 'Phone number', 'anudan' ) : 'Phone number'
	);

	const list = document.createElement( 'div' );

	list.className = 'anudan-checkout__phone-countries';
	list.setAttribute( 'role', 'listbox' );
	list.hidden = true;

	const search = document.createElement( 'input' );

	search.type = 'search';
	search.className = 'anudan-checkout__phone-search';
	search.setAttribute(
		'aria-label',
		window.wp && window.wp.i18n ? window.wp.i18n.__( 'Search countries', 'anudan' ) : 'Search countries'
	);

	const options = document.createElement( 'div' );

	list.appendChild( search );
	list.appendChild( options );

	function renderPrefixLabel() {
		prefix.textContent = '';

		const flag = document.createElement( 'span' );

		flag.setAttribute( 'aria-hidden', 'true' );
		flag.textContent = selected.flag;

		const dial = document.createElement( 'span' );

		dial.textContent = `+${ selected.dial }`;

		prefix.appendChild( flag );
		prefix.appendChild( dial );
		prefix.setAttribute(
			'aria-label',
			`${ selected.name } +${ selected.dial }`
		);
	}

	function emitChange() {
		const digits = number.value.replace( /\D/g, '' );

		onChange( digits === '' ? '' : `+${ selected.dial }${ digits }` );
	}

	/*
	 * R2-10 (rc2): the number in the chosen country's own style as it is typed ("(516) 761-1614"), and a plain word when it is
	 * not a number there, from the phone checker (assets/src/js/phone.js), loaded the first time the box is used.
	 */
	const hint = document.createElement( 'p' );

	hint.className = 'anudan-checkout__field-help anudan-checkout__phone-hint';
	hint.setAttribute( 'aria-live', 'polite' );
	hint.hidden = true;

	const checker = () => ( window.AnudanEngine && window.AnudanEngine.phone ) || null;

	function loadChecker() {
		const engine = window.AnudanEngine || {};
		const src = engine.config && engine.config.phoneScript;

		if ( checker() || loadChecker.started || ! src ) {
			return;
		}

		loadChecker.started = true;

		const script = document.createElement( 'script' );

		script.src = src;
		script.async = true;
		document.head.appendChild( script );
	}

	/*
	 * D3 (0.9.41-rc3): editing works on the digits, never on the brackets, spaces and dashes the style adds. The style used to be
	 * put back after every key, so Backspace after "(929)" removed only the ")", which came straight back: the donor could not
	 * delete the area code. Now a Backspace or Delete that removed only a style character removes the digit beside it; the caret
	 * stays after the same digit wherever it is (typing, deleting, selecting and replacing, pasting, mid-number); and no more
	 * digits are taken than the country's numbers can have (libphonenumber's metadata), 15 before the checker has loaded.
	 */
	let lastDigits = '';
	let lastLength = 0;

	const MOST_DIGITS = 15;

	const digitsOf = ( text ) => String( text || '' ).replace( /\D/g, '' );

	function tooLong( digits ) {
		const phone = checker();

		return phone && 'function' === typeof phone.tooLong ? phone.tooLong( digits, selected.iso2 ) : digits.length > MOST_DIGITS;
	}

	function caretAfterDigits( text, count ) {
		if ( count <= 0 ) {
			// Before the first digit: after an opening bracket, never before it.
			const first = text.search( /\d/ );

			return first < 0 ? text.length : first;
		}

		let seen = 0;

		for ( let index = 0; index < text.length; index += 1 ) {
			if ( /\d/.test( text[ index ] ) ) {
				seen += 1;

				if ( seen === count ) {
					return index + 1;
				}
			}
		}

		return text.length;
	}

	function reformat( event ) {
		const phone = checker();
		const raw = number.value;
		const caret = 'number' === typeof number.selectionStart && null !== number.selectionStart ? number.selectionStart : raw.length;
		const kind = event && event.inputType ? String( event.inputType ) : '';
		let digits = digitsOf( raw );
		let before = digitsOf( raw.slice( 0, caret ) ).length;

		// Only a bracket, space or dash went: the digit beside it goes too.
		if ( digits === lastDigits && raw.length < lastLength && kind.startsWith( 'delete' ) ) {
			if ( 'deleteContentBackward' === kind && before > 0 ) {
				digits = digits.slice( 0, before - 1 ) + digits.slice( before );
				before -= 1;
			} else if ( 'deleteContentForward' === kind && before < digits.length ) {
				digits = digits.slice( 0, before ) + digits.slice( before + 1 );
			}
		}

		// No more digits than the country's numbers have: the ones just typed or pasted past that are not taken.
		while ( digits.length > 0 && tooLong( digits ) ) {
			const at = before > 0 ? before - 1 : digits.length - 1;

			digits = digits.slice( 0, at ) + digits.slice( at + 1 );
			before = Math.min( before, at );
		}

		const shown = '' === digits ? '' : ( phone ? phone.format( digits, selected.iso2 ) : digits );

		lastDigits = digits;

		if ( shown !== raw ) {
			number.value = shown;
		}

		lastLength = number.value.length;

		if ( document.activeElement === number && 'function' === typeof number.setSelectionRange ) {
			const at = caretAfterDigits( number.value, before );

			number.setSelectionRange( at, at );
		}
	}

	function judge() {
		const phone = checker();
		const digits = number.value.replace( /\D/g, '' );

		if ( ! phone || '' === digits ) {
			hint.hidden = true;

			return;
		}

		const ok = phone.check( digits, selected.iso2 ).valid;

		hint.hidden = ok;
		hint.textContent = ok ? '' : ( window.wp && window.wp.i18n ? window.wp.i18n.__( 'That is not a phone number in %s. Check the number, or choose its country.', 'anudan' ) : 'That is not a phone number in %s. Check the number, or choose its country.' ).replace( '%s', selected.name );
	}

	number.addEventListener( 'focus', loadChecker );
	number.addEventListener( 'input', reformat );
	number.addEventListener( 'blur', judge );
	document.addEventListener( 'anudan:phone-ready', reformat );

	function renderOptions( term ) {
		options.textContent = '';

		searchCountries( term ).forEach( ( country ) => {
			const option = document.createElement( 'button' );

			option.type = 'button';
			option.className = 'anudan-checkout__phone-option';
			option.setAttribute( 'role', 'option' );
			option.textContent = `${ country.flag } ${ country.name } +${ country.dial }`;

			option.addEventListener( 'click', () => {
				selected = country;
				renderPrefixLabel();
				closeList();
				// R2-10: the number in the new country's style.
				number.value = number.value.replace( /\D/g, '' );
				reformat();
				emitChange();
				number.focus();
			} );

			options.appendChild( option );
		} );
	}

	function openList() {
		list.hidden = false;
		prefix.setAttribute( 'aria-expanded', 'true' );
		renderOptions( '' );
		search.value = '';
		search.focus();
	}

	function closeList() {
		list.hidden = true;
		prefix.setAttribute( 'aria-expanded', 'false' );
	}

	prefix.addEventListener( 'click', () => {
		if ( list.hidden ) {
			openList();
		} else {
			closeList();
		}
	} );

	search.addEventListener( 'input', () => renderOptions( search.value ) );

	search.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key ) {
			event.stopPropagation();
			closeList();
			prefix.focus();
		}
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( ! wrapper.contains( event.target ) ) {
			closeList();
		}
	} );

	number.addEventListener( 'input', emitChange );

	renderPrefixLabel();

	wrapper.appendChild( prefix );
	wrapper.appendChild( number );
	wrapper.appendChild( list );
	wrapper.appendChild( hint );

	return {
		element: wrapper,
		/**
		 * The ISO 3166-1 alpha-2 code of the country selected in the prefix.
		 *
		 * @return {string}
		 */
		getCountry() {
			return selected.iso2;
		},
		getValue() {
			const digits = number.value.replace( /\D/g, '' );

			return digits === '' ? '' : `+${ selected.dial }${ digits }`;
		},
		setValue( value ) {
			const match = /^\+(\d+)(\d*)$/.exec( String( value || '' ) );

			if ( ! match ) {
				return;
			}

			const found = COUNTRIES.find( ( country ) => match[ 1 ].startsWith( country.dial ) );

			if ( found ) {
				selected = countryByIso2( found.iso2 );
				number.value = match[ 1 ].slice( found.dial.length ) + match[ 2 ];
				renderPrefixLabel();
			}
		},
	};
}
